import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { RootState } from "../redux/slices/store";
import { getData } from "../api/ApiServices";
import DetailsPopUp from "../view/DetailsPopup";

const customStyles = `
  .table-container {
    margin-top: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 15px;
    background-color: #fff;
  }
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
  }
  .table-wrapper {
    position: relative;
   
  }
  .no-records-message {
    padding: 10px;
    text-align: center;
    font-size: 15px;
    color: #666;
    background-color: #f9f9f9;
    border-radius: 4px;
    margin: 10px 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80%;
  }
  .error-message {
    color: #d32f2f;
    padding: 10px;
    background-color: #ffebee;
    border-radius: 4px;
    margin-bottom: 15px;
  }
`;

const CurrentStocks: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [currentStocksData, setCurrentStocksData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]); 
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNoRecords, setHasNoRecords] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  

  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [lastFilterState, setLastFilterState] = useState<any>({});

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [selectedPartId, setSelectedPartId] = useState<string>("");

  const handleCurrentStockClick = (partId: string) => {
    setSelectedPartId(partId);
    setIsPopupOpen(true);
  };
 
  

  const columns = [
    {
      key: "partId",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) =>
        typeof value === "object" ? value?.internalPartNo : "N/A",
      sortable: true,
    },
    {
      key: "partId",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        typeof value === "object" ? value?.partName : "N/A",
      sortable: true,
    },
    {
      key: "partId",
      label: "Model",
      nameKey: "modelId.name",
      render: (value: any) =>
        typeof value === "object" ? value?.modelId?.name : "N/A",
      sortable: true,
    },
    {
      key: "partId",
      label: "Category",
      nameKey: "categoryId.name",
      render: (value: any) =>
        typeof value === "object" ? value?.categoryId?.name : "N/A",
      sortable: true,
    },
    {
      key: "uoms",
      label: "UOM",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
    },
    { key: "qty", label: "Current Stock", sortable: true ,isRightAlign: true, 
      render: (value: number, rowData: any) => (
        <span 
          className="clickable-cell"
          onClick={(e) => {
            e.stopPropagation();
            if (rowData.partId && rowData.partId._id) {
              console.log("sgasb",rowData.partId._id);
              handleCurrentStockClick(rowData.partId._id);
            }
          }}
        >
          {value}
        </span>
      )
    },
    { key: "stockStatus", label: "Status", sortable: false },
  ];

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

 
  useEffect(() => {
  
    const hasFilterChanged = JSON.stringify(filters) !== JSON.stringify(lastFilterState);
    
    if (hasFilterChanged) {
      setIsFiltering(Object.keys(filters).length > 0);
      setLastFilterState(filters);
      

      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {

        fetchCurrentStocks();
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchCurrentStocks();
  }, [currentPage, itemsPerPage, sortBy, sortOrder]);

  const fetchCurrentStocks = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response: any = await getData("/stocks", {
        page: currentPage,
        limit: itemsPerPage,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      
      console.log("API Response:", response);
      
      if (response?.data?.data) {
        setCurrentStocksData(response.data.data);
        
      
        if (currentPage === 1 && !isFiltering && response.data.data.length > 0) {
          setOriginalData(response.data.data);
        }
        
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
        setHasNoRecords(response.data.data.length === 0);
      } else {
       
        setCurrentStocksData([]);
        setHasNoRecords(true);
      }
    } catch (err) {
      console.error("Error fetching stock data:", err);
      setError("Failed to load current stocks data. Please try again later.");
      setCurrentStocksData([]);
      setTotalItems(0);
      setTotalPages(0);
      setHasNoRecords(true);
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleFilterChange = useCallback((filters: Record<string, any>) => {
    Object.entries(filters).forEach(([key, value]) => {
      dispatch(updateFilters({ key, value: String(value) }));
    });
  }, [dispatch]);

  return (
    <div className="item-models-container">
      <style>{customStyles}</style>

      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Stocks</span>
              <span>Current Stocks</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="box-container">
        <div className="table-wrapper">
          <TableComponent
            columns={columns}
            data={currentStocksData}
            setFilter={handleFilterChange}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={["uoms"]}
            context="currentStocks"
          />
          
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
            </div>
          )}
          
         
        </div>
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          totalItems={totalItems}
        />
      </div>
      <DetailsPopUp
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        partId={selectedPartId}
        isStockSummary={false}
        // startDate={StartDate ? StartDate.toISOString().split("T")[0] : undefined}
        // endDate={EndDate ? EndDate.toISOString().split("T")[0] : undefined}
        />
    </div>
  );
};

export default CurrentStocks;